import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BillingToggle } from "@/components/billing/BillingToggle";
import { useBillingOptions, findOption, savingsPct } from "@/lib/billing";
import { fadeUp } from "./anim";

// Static plan metadata (features + copy). Prices come from the live billing options
// so the 6-month and 12-month recurring options are shown with their real amounts.
const PLANS = [
  {
    id: "free",
    name: "Free",
    features: ["10 assessments / month", "Basic risk score", "Basic reports"],
    cta: "Start free",
    highlight: false,
  },
  {
    id: "pro",
    name: "Pro Host",
    features: [
      "Unlimited risk assessments",
      "Downloadable PDF evidence reports",
      "Evidence Centre & saved reports",
      "Risk analytics dashboard",
    ],
    cta: "Choose Pro Host",
    highlight: true,
    fallback: { amount: 19, interval_label: "/month" },
  },
  {
    id: "pm",
    name: "Property Manager",
    features: [
      "Everything in Pro Host",
      "Multi-property management",
      "Portfolio analytics",
      "Team access & roles",
      "Advanced reporting",
    ],
    cta: "Choose Property Manager",
    highlight: false,
    fallback: { amount: 99, interval_label: "/month" },
  },
];

const fmt = (n) => (Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`);

export const LandingPricing = () => {
  const [interval, setInterval] = useState("monthly");
  const { options } = useBillingOptions();

  // Highest genuine saving (vs monthly) for the selected interval — used for the
  // best-value caption. Computed only from real configured prices.
  const proSaving = savingsPct(options, "pro", interval);

  const priceBlock = (plan) => {
    if (plan.id === "free") {
      return { price: "$0", period: "forever", perMonth: null, save: 0 };
    }
    const opt = findOption(options, plan.id, interval);
    if (!opt) {
      // Options still loading — show the monthly fallback so the card never looks empty.
      return { price: fmt(plan.fallback.amount), period: plan.fallback.interval_label, perMonth: null, save: 0 };
    }
    return {
      price: fmt(opt.amount),
      period: opt.interval_label,
      perMonth: interval !== "monthly" ? opt.per_month_equivalent : null,
      save: savingsPct(options, plan.id, interval),
    };
  };

  return (
    <section id="pricing" className="border-y bg-secondary/40">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <motion.div {...fadeUp} className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-semibold tracking-tight">Simple, transparent pricing</h2>
          <p className="mt-3 text-muted-foreground">
            Start free. Upgrade when you need AI summaries and unlimited checks. Prices in AUD.
          </p>
        </motion.div>

        {/* Billing period selector — Monthly / 6 Months / 12 Months */}
        <div className="mt-8 flex flex-col items-center gap-2">
          <BillingToggle
            value={interval}
            onChange={setInterval}
            options={options}
            savingsPlan="pro"
            bestValueInterval="12mo"
            className="mt-1"
          />
          <p className="min-h-[20px] text-xs text-muted-foreground" data-testid="landing-pricing-interval-caption">
            {interval === "monthly"
              ? "Billed monthly, cancel anytime."
              : `Pay upfront, billed ${findOption(options, "pro", interval)?.interval_label || ""} — renews automatically${proSaving > 0 ? ` · save up to ${proSaving}%` : ""}.`}
          </p>
        </div>

        <div className="mt-8 grid gap-5 lg:grid-cols-3">
          {PLANS.map((p) => {
            const pricing = priceBlock(p);
            const query = p.id === "free" ? "" : `?plan=${p.id}&interval=${interval}`;
            return (
              <div
                key={p.id}
                data-testid={`landing-pricing-${p.id}`}
                className={`relative flex flex-col rounded-2xl border bg-card p-6 transition-shadow ${
                  p.highlight ? "border-primary/30 shadow-[0_10px_30px_rgba(15,23,42,0.10)]" : ""
                }`}
              >
                <div className="flex items-center gap-2">
                  {p.highlight && (
                    <span className="inline-flex w-fit items-center gap-1 rounded-full bg-primary px-2.5 py-0.5 text-xs font-medium text-primary-foreground">
                      <Sparkles className="h-3 w-3" /> Most popular
                    </span>
                  )}
                  {p.id !== "free" && interval === "12mo" && pricing.save > 0 && (
                    <span
                      className="inline-flex w-fit items-center rounded-full bg-[hsl(var(--risk-low-bg))] px-2.5 py-0.5 text-xs font-semibold text-[hsl(var(--risk-low))]"
                      data-testid={`landing-pricing-save-${p.id}`}
                    >
                      Save {pricing.save}%
                    </span>
                  )}
                </div>

                <h3 className="mt-3 font-display text-lg font-semibold">{p.name}</h3>

                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-display text-4xl font-semibold" data-testid={`landing-price-${p.id}`}>
                    {pricing.price}
                  </span>
                  <span className="text-sm text-muted-foreground">{pricing.period}</span>
                </div>
                {pricing.perMonth ? (
                  <p className="mt-1 text-xs text-[hsl(var(--risk-low))]" data-testid={`landing-permonth-${p.id}`}>
                    ≈ {fmt(pricing.perMonth)}/month equivalent
                  </p>
                ) : (
                  <p className="mt-1 h-4" />
                )}

                <ul className="mt-5 flex-1 space-y-2.5 text-sm">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--risk-low))]" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6">
                  <Link to={`/signup${query}`}>
                    <Button
                      variant={p.highlight ? "default" : "outline"}
                      className="w-full"
                      data-testid={`landing-pricing-${p.id}-button`}
                    >
                      {p.cta}
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
